import { ContactRecord } from "@/models/contact";
import { getDefaultContactDirectoryEntity } from "@/models/contactDirectory";
import {
  Button,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { BiCog } from "react-icons/bi";
import { TagInputEditor } from "../TagInput/TagInputEditor";
import { TagPopoverEditor } from "../TagPopoverEditor/TagPopoverEditor";

export const EditContactForm = ({
  initialValues,
  onSubmit,
  callToAction = "Add Contact",
  onValuesChange,
}: {
  initialValues?: Partial<ContactRecord>;
  onSubmit?: (values: Partial<ContactRecord>) => void;
  callToAction?: string;
  onValuesChange?: (values: Partial<ContactRecord>) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const contactDirectortEntity = getDefaultContactDirectoryEntity();
  const observeRecord = useAtomValue(
    contactDirectortEntity.getObserveEntityRecordAtom(),
  );
  const jobTitleTags = observeRecord?.data?.jobTitleTags || [];

  const form = useForm({
    mode: "uncontrolled",

    initialValues: {
      email: "",
      phone: "",
      location: "",
      website: "",
      ...initialValues,
    } satisfies Partial<ContactRecord>,
    onValuesChange: (values) => {
      onValuesChange?.(values);
    },
  });

  const handleSubmit = async (values: Partial<ContactRecord>) => {
    if (!onSubmit) return;
    setLoading(true);
    await onSubmit(values);
    setLoading(false);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap={"xs"} mb={"lg"}>
        <SimpleGrid cols={2}>
          <Stack gap={"xs"}>
            <TextInput
              label="Name"
              data-autofocus
              key={form.key("name")}
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Email"
              data-autofocus
              key={form.key("email")}
              {...form.getInputProps("email")}
            />
            <TextInput
              label="Phone"
              key={form.key("phone")}
              {...form.getInputProps("phone")}
            />
            <TextInput
              label="Website"
              key={form.key("website")}
              {...form.getInputProps("website")}
            />{" "}
            <TextInput
              label="Remarks"
              key={form.key("remarks")}
              {...form.getInputProps("remarks")}
            />
          </Stack>
          <Stack gap={"xs"}>
            <TextInput
              label="Location"
              key={form.key("location")}
              {...form.getInputProps("location")}
            />
            <TextInput
              label="Firm / Organization"
              data-autofocus
              key={form.key("firm")}
              {...form.getInputProps("firm")}
            />
            <TagInputEditor
              label={
                <Group w={200} h={12} gap={"sm"}>
                  <Text fw={500} fz={14} lh={1}>
                    Roles
                  </Text>
                  <TagPopoverEditor
                    entity={getDefaultContactDirectoryEntity()}
                    resourceProp={"jobTitleTags"}
                  >
                    <BiCog />
                  </TagPopoverEditor>
                </Group>
              }
              value={form.getValues().jobTitleIds || []}
              onValueChange={(value) => {
                form.setFieldValue("jobTitleIds", value);
              }}
              callToAction="Add Role..."
              tagOptions={jobTitleTags}
            />
          </Stack>
        </SimpleGrid>

        {onSubmit && (
          <>
            <Button type="submit" fullWidth loading={loading} mt="md">
              {callToAction}
            </Button>
            <Text ta={"center"} size={"xs"} c={"dimmed"} px={"xl"}>
              Everywhere this contact appears will be updated.
            </Text>
          </>
        )}
      </Stack>
    </form>
  );
};
